import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Divider,
    Typography,
    useMediaQuery,
} from '@mui/material';
import moment from 'moment';
import { theme } from '../../../../../shared/theme';

type Props = {
    reservation: any;
    expanded: boolean;
    onToggle: (event: React.SyntheticEvent, isExpanded: boolean) => void;
};

export default function ReservationHistory({
    reservation,
    expanded,
    onToggle,
}: Props) {
    const isMobile = useMediaQuery('(max-width: 770px)');

    const formatDate = (date: any, format: string = 'DD/MM/YYYY') =>
        date ? moment(date).format(format) : '';

    const isOwnerBooking =
        String(reservation?.bookerType || '').toLowerCase() === 'owner';

    const cardSx = {
        backgroundColor: theme.palette.custom.cardActionBackground,
        borderRadius: 2,
        px: isMobile ? 1.5 : 2,
        py: 1.5,
    };

    const rowSx = {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(140px, 180px) 1fr',
        gap: isMobile ? 0.25 : 2,
        py: 0.75,
        borderTop: `1px solid ${theme.palette.custom.lightBorderColor}`,
    };

    const labelSx = {
        fontWeight: 700,
        color: theme.palette.text.secondary,
    };

    const valueSx = {
        color: theme.palette.text.primary,
        wordBreak: 'break-word' as const,
        lineHeight: 1.4,
    };

    const outerAccordionSx = {
        mt: 2,
        borderRadius: 2,
        boxShadow: 'none',
        '&::before': { display: 'none' },
        overflow: 'hidden',
    };

    const summarySx = {
        py: 1.25,
        backgroundColor: theme.palette.neutral.main,
    };

    const detailsSx = {
        pb: isMobile ? 2 : 3,
        px: 0,
        pt: 0,
        backgroundColor: theme.palette.neutral.main,
    };

    return (
        <Accordion
            expanded={expanded}
            onChange={onToggle}
            disableGutters
            square={false}
            sx={outerAccordionSx}
        >
            <AccordionSummary
                aria-controls='reservation-history-content'
                id='reservation-history-header'
                sx={{ ...summarySx, px: 0 }}
            >
                <Typography
                    variant='h2'
                    sx={{ letterSpacing: 0.2, color: 'text.secondary' }}
                >
                    History
                </Typography>
            </AccordionSummary>

            <AccordionDetails sx={detailsSx}>
                <Divider sx={{ mt: 0, mb: 2 }} />

                <Box sx={cardSx}>
                    <Box sx={{ ...rowSx, borderTop: 'none', pt: 0.25 }}>
                        <Typography variant='body2' sx={labelSx}>
                            Created
                        </Typography>
                        <Typography variant='body2' sx={valueSx}>
                            {formatDate(
                                reservation?.createdAt,
                                'DD/MM/YYYY, h:mm a',
                            )}{' '}
                            – {reservation?.createdBy?.firstName}{' '}
                            {reservation?.createdBy?.lastName} –{' '}
                            {reservation?.createdBy?.email}
                        </Typography>
                    </Box>

                    {reservation?.editedAt != null && (
                        <Box sx={rowSx}>
                            <Typography variant='body2' sx={labelSx}>
                                Last edited
                            </Typography>
                            <Typography variant='body2' sx={valueSx}>
                                {formatDate(
                                    reservation?.editedAt,
                                    'DD/MM/YYYY, h:mm a',
                                )}{' '}
                                – {reservation?.editedBy?.firstName}{' '}
                                {reservation?.editedBy?.lastName} –{' '}
                                {reservation?.editedBy?.email}
                            </Typography>
                        </Box>
                    )}

                    {reservation?.declinedAt != null && (
                        <>
                            <Box sx={rowSx}>
                                <Typography variant='body2' sx={labelSx}>
                                    Declined:
                                </Typography>
                                <Typography variant='body2' sx={valueSx}>
                                    {formatDate(
                                        reservation?.declinedAt,
                                        'DD/MM/YYYY, h:mm a',
                                    )}
                                </Typography>
                            </Box>

                            {reservation?.declineReason != null && (
                                <Box
                                    sx={{
                                        ...rowSx,
                                        borderTop: 'none',
                                        pt: 0,
                                    }}
                                >
                                    <Box />
                                    <Typography variant='body2' sx={valueSx}>
                                        <strong>Decline Reason:</strong>{' '}
                                        {reservation.declineReason}
                                    </Typography>
                                </Box>
                            )}

                            {reservation?.declineComment != null && (
                                <Box
                                    sx={{
                                        ...rowSx,
                                        borderTop: 'none',
                                        pt: 0,
                                    }}
                                >
                                    <Box />
                                    <Typography variant='body2' sx={valueSx}>
                                        <strong>Comment:</strong>{' '}
                                        {reservation.declineComment}
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                    {reservation?.approvedAt != null && (
                        <Box sx={rowSx}>
                            <Typography variant='body2' sx={labelSx}>
                                Approved
                            </Typography>
                            <Typography variant='body2' sx={valueSx}>
                                {formatDate(
                                    reservation?.approvedAt,
                                    'DD/MM/YYYY, h:mm a',
                                )}{' '}
                                –{' '}
                                {isOwnerBooking
                                    ? 'Auto approved (owner booking)'
                                    : reservation?.approvedBy?.firstName == null
                                      ? 'The host has auto-approval turned on, so this booking was confirmed instantly.'
                                      : `${reservation?.approvedBy?.firstName ?? ''} ${reservation?.approvedBy?.lastName ?? ''} – ${reservation?.approvedBy?.email ?? ''}`}
                            </Typography>
                        </Box>
                    )}

                    {reservation?.cancelledAt != null && (
                        <>
                            <Box sx={rowSx}>
                                <Typography variant='body2' sx={labelSx}>
                                    Cancelled:
                                </Typography>
                                <Typography variant='body2' sx={valueSx}>
                                    {formatDate(
                                        reservation?.cancelledAt,
                                        'DD/MM/YYYY, h:mm a',
                                    )}{' '}
                                    – {reservation?.cancelledBy?.firstName}{' '}
                                    {reservation?.cancelledBy?.lastName} –{' '}
                                    {reservation?.cancelledBy?.email}
                                </Typography>
                            </Box>

                            {reservation?.cancellationReason != null && (
                                <Box
                                    sx={{
                                        ...rowSx,
                                        borderTop: 'none',
                                        pt: 0,
                                    }}
                                >
                                    <Box />
                                    <Typography variant='body2' sx={valueSx}>
                                        <strong>Cancellation Reason:</strong>{' '}
                                        {reservation.cancellationReason}
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                    {reservation?.status === 'completed' && (
                        <Box sx={rowSx}>
                            <Typography variant='body2' sx={labelSx}>
                                Completed
                            </Typography>
                            <Typography variant='body2' sx={valueSx}>
                                {formatDate(reservation?.endDate, 'DD/MM/YYYY')}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
}
